/**
 * №16.2.1 — the DRIVE lens of the full process view (mockup 16.2.1-fullview-v3).
 *
 * Left: the spine — a start pill, per-segment cards with their trigger chip and
 * step rows, DONE segments collapsed to a dated line (expandable), the decision
 * inline as option TILES with a client-side branch PREVIEW (hatched, no verb), and
 * Change/Clear. Right: the shared PROPERTIES RAIL (DO NEXT on top, then the
 * selected element). The rail + the inline popovers live here and the Plan lens
 * imports them, so there is exactly one rail and one set of editors; the pure
 * derivations + cross-lens types live in `wf-fullview-shared.ts`.
 *
 * Render `state` for a SEGMENT (never the raw engine `status`); render `status`
 * for a STEP (that is the step's own literal). Execution only — no authoring.
 */
import { useEffect, useRef, useState } from "react";
import type { DetailSegment, DetailStep, WaitingStep, DeclaredEffect } from "../../hooks/use-workflow";
import { agentPersonaFromId, personaFill } from "../../lib/agent-persona";
import { TickGlyph } from "./wf-glyphs";
import {
  resolveAssignee, fmtDate, isOverdue, stepIsDone, openSteps,
  triggerText, openedDate, branchTail, initialsOf, prettyDateType, outcomeColorVar,
  type Selection, type Preview, type Directory, type LensProps, type WorkflowActions,
} from "./wf-fullview-shared";

/** The per-outcome colour, packaged as the `--wfv-oc` custom property for inline
 *  style (the key is cast so TS accepts the CSS variable). */
function ocStyle(colorVar: string): React.CSSProperties {
  return { ["--wfv-oc"]: colorVar } as React.CSSProperties;
}

/** RC1-1 — the identity fill for a raw `.wfv-avm.is-agent` span, keyed on the
 *  assignee's node id so an agent's workflow avatar is the SAME colour as its rail
 *  avatar. Returns undefined for people/roles (they keep the neutral avatar). */
function agentAvatarStyle(assigneeKind: string | undefined, assigneeCode: string | undefined): React.CSSProperties | undefined {
  if (assigneeKind !== "agent" || !assigneeCode) return undefined;
  return { background: personaFill(agentPersonaFromId(assigneeCode)), color: "#fff" };
}

// ─── shared popovers (inline editors) ───────────────────────────────────────

function useOutsideClose(onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onDown(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onClose]);
  return ref;
}

const ROLE_OPTIONS: { code: string; label: string }[] = [
  { code: "assigned", label: "Assigned lawyer" },
  { code: "assisting", label: "Assisting lawyer" },
  { code: "manager", label: "Manager" },
];

export function AssigneePopover({ dir, selectedKind, selectedCode, busy, onPick, onClose }: {
  dir: Directory; selectedKind?: string; selectedCode?: string; busy: boolean;
  onPick: (kind: string, code: string | null) => void; onClose: () => void;
}) {
  const ref = useOutsideClose(onClose);
  const agents = Object.entries(dir.agentNames);
  return (
    <div className="wfv-popover" ref={ref} onClick={(e) => e.stopPropagation()}>
      <div className="hd2">Assign to</div>
      {dir.members.filter((m) => m.status !== "pending").map((m) => (
        <button key={m.id} className={`wfv-popt ${selectedKind === "person" && selectedCode === m.id ? "is-sel" : ""}`} disabled={busy} onClick={() => onPick("person", m.id)}>
          <span className="wfv-avm">{initialsOf(m.name || m.email)}</span>{m.name || m.email}
          <span className="sub">{m.id === dir.currentUserId ? "you" : m.role}</span>
        </button>
      ))}
      {ROLE_OPTIONS.map((r) => (
        <button key={r.code} className={`wfv-popt ${selectedKind === "role" && selectedCode === r.code ? "is-sel" : ""}`} disabled={busy} onClick={() => onPick("role", r.code)}>
          <span className="wfv-avm">{initialsOf(r.label)}</span>{r.label}<span className="sub">role</span>
        </button>
      ))}
      {agents.map(([id, name]) => (
        <button key={id} className={`wfv-popt ${selectedKind === "agent" && selectedCode === id ? "is-sel" : ""}`} disabled={busy} onClick={() => onPick("agent", id)}>
          <span className="wfv-avm is-agent" style={agentAvatarStyle("agent", id)}>✦</span>{name}<span className="sub">agent · will draft</span>
        </button>
      ))}
      <button className="wfv-popt" disabled={busy} onClick={() => onPick("unassigned", null)}>
        <span className="wfv-avm">—</span>Unassigned
      </button>
    </div>
  );
}

export function DuePopover({ ruleLabel, currentDate, busy, onPick, onClose }: {
  ruleLabel: string; currentDate: string | null; busy: boolean;
  onPick: (date: string | null) => void; onClose: () => void;
}) {
  const ref = useOutsideClose(onClose);
  return (
    <div className="wfv-popover" ref={ref} style={{ minWidth: 190 }} onClick={(e) => e.stopPropagation()}>
      <div className="hd2">Due date</div>
      {/* Disabled — the derived date rule is NOT built per the 16.2.1 ruling. */}
      <button className="wfv-popt" disabled title="Coming with date rules">{ruleLabel}<span className="sub">rule</span></button>
      <label className="wfv-popt" onClick={(e) => e.stopPropagation()}>
        Pick a date…
        <input type="date" value={currentDate ?? ""} disabled={busy} onClick={(e) => e.stopPropagation()} onChange={(e) => onPick(e.target.value || null)} />
      </label>
      <button className="wfv-popt" disabled={busy} onClick={() => onPick(null)} style={{ color: "var(--text-3)" }}>Clear</button>
    </div>
  );
}

const WORK_STATES: { code: string; label: string }[] = [
  { code: "todo", label: "To do" },
  { code: "in_progress", label: "In progress" },
  { code: "blocked", label: "Blocked" },
  { code: "waiting", label: "Waiting" },
];

export function WorkStatePopover({ current, busy, onPick, onClose }: {
  current?: string; busy: boolean; onPick: (state: string) => void; onClose: () => void;
}) {
  const ref = useOutsideClose(onClose);
  return (
    <div className="wfv-popover" ref={ref} style={{ minWidth: 170 }} onClick={(e) => e.stopPropagation()}>
      <div className="hd2">Work state</div>
      {WORK_STATES.map((w) => (
        <button key={w.code} className={`wfv-popt ${current === w.code ? "is-sel" : ""}`} disabled={busy} onClick={() => onPick(w.code)}>{w.label}</button>
      ))}
    </div>
  );
}

// ─── the shared PROPERTIES RAIL ─────────────────────────────────────────────

export function PropertiesRail({ data, actions, busy, selected, onSelect, preview, onPreview }: LensProps) {
  const segments = data.detail.segments;
  const na = data.next.nextAction;
  const [editAssignee, setEditAssignee] = useState(false);
  const [editDue, setEditDue] = useState(false);
  const [editWork, setEditWork] = useState(false);

  const sel = selected;
  const selSeg = sel ? segments.find((s) => s.segmentNo === sel.segmentNo) ?? null : null;
  const selStep = sel?.kind === "step" && selSeg ? selSeg.steps.find((s) => s.stepEntryNo === sel.stepEntryNo) ?? null : null;

  // D-RS6 — the declared WRITE effects of whatever is selected, for the "When this
  // happens" rail. A step shows its own; a decision shows the segment's decision +
  // per-outcome stamps (from the flat instance list); a stage shows its onOpen.
  const selectedEffects: DeclaredEffect[] = !sel ? []
    : sel.kind === "step" ? (selStep?.effects ?? [])
    : sel.kind === "decision" ? (data.detail.effects ?? []).filter((e) => e.ownerNo === sel.segmentNo && (e.ownerKind === "decision" || e.ownerKind === "outcome"))
    : (selSeg?.effects ?? []);

  // ── DO NEXT ──
  const doNext = na && na.kind !== "none" ? (
    <div className="wfv-donext">
      <div className="k">DO NEXT</div>
      <div className="t">{na.title}</div>
      <div className="why">
        {na.why}
        {na.assigneeKind ? <> · {resolveAssignee(na.assigneeKind, na.assigneeCode, data.directory).who}</> : null}
        {na.overdue ? <span className="is-od"> · overdue</span> : na.atRisk ? <span className="is-risk"> · at risk</span> : null}
      </div>
      {na.instructions ? <div className="instr">{na.instructions}</div> : null}
      {na.kind === "decision"
        ? <button className="btn btn--primary" disabled={busy} onClick={() => { onSelect({ kind: "decision", segmentNo: na.segmentNo }); scrollToSeg(na.segmentNo); }}>Decide</button>
        : <button className="btn btn--primary" disabled={busy} onClick={() => actions.complete(na.stepEntryNo)}>{na.verb || "Complete"}</button>}
    </div>
  ) : null;

  return (
    <aside className="wfv-rail">
      {doNext}

      {!sel ? (
        <div className="wfv-sect"><p className="wfv-empty">Select any step, stage or decision on the left to see its details here.</p></div>
      ) : sel.kind === "decision" && selSeg?.decision ? (
        <DecisionRail seg={selSeg} segments={segments} actions={actions} busy={busy} preview={preview} onPreview={onPreview} />
      ) : sel.kind === "step" && selStep && selSeg ? (
        <StepRail
          seg={selSeg} step={selStep} segments={segments} dir={data.directory} actions={actions} busy={busy}
          editAssignee={editAssignee} setEditAssignee={setEditAssignee}
          editDue={editDue} setEditDue={setEditDue} editWork={editWork} setEditWork={setEditWork}
        />
      ) : selSeg ? (
        <SegmentRail seg={selSeg} segments={segments} />
      ) : null}

      <div className="wfv-hr" />
      <div className="wfv-sect">
        <div className="k">When this happens</div>
        {/* D-RS6 — the REAL declared WRITE effects of the selected element (from the
            instance's pinned snapshot), replacing the honest-empty placeholder. A
            reporting write (setAttribute) reads off onto the matter's declared
            fields; a decision shows its per-outcome stamps. */}
        <EffectsList effects={selectedEffects} />
      </div>

      <div className="wfv-hr" />
      <div className="wfv-sect">
        <div className="k">Waiting on the outside world</div>
        <WaitingList waits={data.next.waiting} actions={actions} busy={busy} />
      </div>

      <div className="wfv-hr" />
      <MatterStats segments={segments} />
    </aside>
  );
}

function DecisionRail({ seg, segments, actions, busy, preview, onPreview }: {
  seg: DetailSegment; segments: DetailSegment[]; actions: WorkflowActions; busy: boolean;
  preview: Preview | null; onPreview: (p: Preview | null) => void;
}) {
  const dec = seg.decision!;
  const chosen = dec.outcomeCode;
  const opened = openedDate(seg, segments);
  const previewOutcome = preview?.decisionSegmentNo === seg.segmentNo ? preview.outcome : null;
  const previewLabel = dec.outcomes.find((o) => o.code === previewOutcome)?.label ?? "";

  // Consequence — ADAPTED from which segments each outcome opens.
  const curBranch = chosen ? branchTail(segments, seg.segmentNo, chosen) : [];
  const curName = curBranch[0]?.name ?? "the current path";
  const curOpen = curBranch.reduce((n, s) => n + openSteps(s).length, 0);
  const newBranch = previewOutcome ? branchTail(segments, seg.segmentNo, previewOutcome) : [];
  const newName = newBranch[0]?.name ?? "the other path";

  return (
    <>
      <div className="wfv-sect">
        <div className="k">Decision</div>
        <div className="big">{dec.name}</div>
      </div>
      <div className="wfv-opens">
        <div className="k2">⏱ Opens when</div>
        {triggerText(seg.trigger, segments)}{opened ? ` — opened ${fmtDate(opened)}` : ""}
      </div>

      {dec.resolved ? (
        <div className="wfv-sect">
          <p className="wfv-subcap">Chosen path — preview another to switch</p>
          {dec.outcomes.map((o) => {
            const isChosen = o.code === chosen;
            const isPrev = o.code === previewOutcome;
            return (
              <button key={o.code} className={`wfv-optrow ${isChosen ? "is-chosen" : ""} ${isPrev ? "is-preview" : ""} ${!isChosen && !isPrev ? "is-untaken" : ""}`}
                style={ocStyle(outcomeColorVar(dec, o.code))}
                onClick={() => onPreview(isChosen || isPrev ? null : { decisionSegmentNo: seg.segmentNo, outcome: o.code })}>
                <span className="d" /><b>{o.label}</b>
                <span className="tag">{isChosen ? "✓ chosen" : isPrev ? "previewing" : "preview"}</span>
              </button>
            );
          })}
          <div className="wfv-railact">
            <button className="btn btn--primary btn--sm" disabled={busy || !previewOutcome}
              onClick={() => { if (previewOutcome) { actions.recordOutcome(seg.segmentNo, previewOutcome); onPreview(null); } }}>
              {previewOutcome ? `Change to “${previewLabel}”` : "Preview an option"}
            </button>
            <button className="btn btn--secondary btn--sm" disabled={busy} onClick={() => { actions.clearOutcome(seg.segmentNo); onPreview(null); }}>Clear</button>
          </div>
          <p className="wfv-expl"><b>Recorded.</b> Preview another option and <b>Change</b> to switch the matter to it; <b>Clear</b> to un-decide.</p>
          {previewOutcome ? (
            <div className="wfv-conseq">Changing closes {curName}’s {curOpen} open step{curOpen === 1 ? "" : "s"} and opens {newName} — nothing is deleted; the {curName} work is kept in history.</div>
          ) : null}
        </div>
      ) : (
        <div className="wfv-sect">
          <p className="wfv-subcap">{dec.ripe ? "Choose the path this matter takes" : "Finish this stage’s work, then choose"}</p>
          {dec.outcomes.map((o) => (
            <button key={o.code} className="wfv-optrow" style={ocStyle(outcomeColorVar(dec, o.code))} disabled={busy || !dec.ripe} onClick={() => actions.recordOutcome(seg.segmentNo, o.code)}>
              <span className="d" /><b>{o.label}</b>
              <span className="tag">{o.isDefault ? "default · decide" : "decide"}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function StepRail({ seg, step, segments, dir, actions, busy, editAssignee, setEditAssignee, editDue, setEditDue, editWork, setEditWork }: {
  seg: DetailSegment; step: DetailStep; segments: DetailSegment[]; dir: Directory; actions: WorkflowActions; busy: boolean;
  editAssignee: boolean; setEditAssignee: (v: boolean) => void;
  editDue: boolean; setEditDue: (v: boolean) => void;
  editWork: boolean; setEditWork: (v: boolean) => void;
}) {
  const asg = resolveAssignee(step.assigneeKind, step.assigneeCode, dir);
  const opened = openedDate(seg, segments);
  const ready = step.status === "ready";
  const dueLabel = step.dueDate ? fmtDate(step.dueDate) : "—";
  const ruleLabel = seg.trigger?.dateType ? `${prettyDateType(seg.trigger.dateType)} rule` : "coming with date rules";

  return (
    <>
      <div className="wfv-sect">
        <div className="k">Step</div>
        <div className="big">{step.name}</div>
      </div>
      <div className="wfv-opens">
        <div className="k2">⏱ {ready ? "Ready because" : "Opens when"}</div>
        “{seg.name}” opens when {triggerText(seg.trigger, segments)}{opened ? ` — opened ${fmtDate(opened)}` : ""}{seg.stepsOrdered ? " · in order" : ""}
      </div>

      {ready ? (
        <button className="btn btn--primary" disabled={busy} onClick={() => actions.complete(step.stepEntryNo)}>{step.verb || "Complete"}</button>
      ) : step.status === "waiting" ? (
        /* RC1-4 — a selected wait is completable here too: Mark received. */
        <button className="btn btn--secondary" disabled={busy} onClick={() => actions.complete(step.stepEntryNo)}>Mark received</button>
      ) : null}

      <div className="wfv-sect">
        <div className="wfv-kv">
          <span>Assignee</span><span className="v">{asg.isAgent ? "✦ " : ""}{asg.who}</span>
          <span className="wfv-pop">
            <button className="edit" disabled={busy} onClick={() => setEditAssignee(!editAssignee)}>change</button>
            {editAssignee ? (
              <AssigneePopover dir={dir} selectedKind={step.assigneeKind} selectedCode={step.assigneeCode} busy={busy}
                onClose={() => setEditAssignee(false)}
                onPick={(kind, code) => { actions.setAssignee(step.stepEntryNo, kind, code); setEditAssignee(false); }} />
            ) : null}
          </span>
        </div>
        <div className="wfv-kv">
          <span>Due</span><span className={`v ${isOverdue(step.dueDate) ? "" : ""}`}>{dueLabel}</span>
          <span className="wfv-pop">
            <button className="edit" disabled={busy} onClick={() => setEditDue(!editDue)}>change</button>
            {editDue ? (
              <DuePopover ruleLabel={ruleLabel} currentDate={step.dueDate ?? null} busy={busy}
                onClose={() => setEditDue(false)}
                onPick={(date) => { actions.setDue(step.stepEntryNo, date); setEditDue(false); }} />
            ) : null}
          </span>
        </div>
        <div className="wfv-kv">
          <span>Work state</span><span className="v">{WORK_STATES.find((w) => w.code === step.status)?.label ?? workLabel(step.status)}</span>
          <span className="wfv-pop">
            <button className="edit" disabled={busy} onClick={() => setEditWork(!editWork)}>change</button>
            {editWork ? (
              <WorkStatePopover current={step.status} busy={busy}
                onClose={() => setEditWork(false)}
                onPick={(state) => { actions.setWorkState(step.stepEntryNo, state); setEditWork(false); }} />
            ) : null}
          </span>
        </div>
      </div>

      <div className="wfv-hr" />
      <InstructionsSection key={step.stepEntryNo} step={step} actions={actions} busy={busy} />

      <div className="wfv-hr" />
      <div className="wfv-sect">
        <div className="k">Evidence</div>
        <span className="wfv-evid">⎙ {step.taskEntryNo ? "a task on the matter carries this" : "none yet — attaches on complete"}</span>
      </div>

      <div className="wfv-hr" />
      <div className="wfv-sect">
        <div className="k">Escapes</div>
        <div className="wfv-esc">
          <button className="btn btn--secondary btn--sm" disabled={busy}
            onClick={() => { const r = window.prompt("Skip this step — why?"); if (r != null) actions.skip(step.stepEntryNo, r); }}>Skip — with reason</button>
          <button className="btn btn--secondary btn--sm" disabled={busy} onClick={() => setEditAssignee(true)}>Reassign</button>
          <button className="btn btn--secondary btn--sm" disabled title="Time-from-plan is coming with date rules">+ Time from plan</button>
        </div>
      </div>
    </>
  );
}

function workLabel(status: string): string {
  return status.replace(/[-_]/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

/** Item 6 — the step briefing. Shown when a STEP is selected, editable inline. */
// Rendered with `key={step.stepEntryNo}`, so switching steps REMOUNTS this and
// the initializers re-seed — no reset-in-effect (the strict react-hooks rule).
export function InstructionsSection({ step, actions, busy }: { step: DetailStep; actions: WorkflowActions; busy: boolean }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(step.instructions ?? "");

  return (
    <div className="wfv-sect">
      <div className="k">
        Instructions
        {step.instructionsDeviation ? <span className="wfv-chip-dev" style={{ marginLeft: 8 }}>edited for this matter</span> : null}
      </div>
      {editing ? (
        <div className="wfv-instr-edit">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="What should whoever holds this step do?" />
          <div className="wfv-instr-actions">
            <button className="btn btn--primary btn--sm" disabled={busy} onClick={() => { actions.setInstructions(step.stepEntryNo, draft); setEditing(false); }}>Save</button>
            <button className="btn btn--ghost btn--sm" disabled={busy} onClick={() => { setDraft(step.instructions ?? ""); setEditing(false); }}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          {step.instructions ? <div className="wfv-instr-view">{step.instructions}</div> : <p className="wfv-empty">No briefing yet.</p>}
          <button className="edit" style={{ marginTop: 6 }} disabled={busy} onClick={() => setEditing(true)}>Edit briefing</button>
        </>
      )}
    </div>
  );
}

function SegmentRail({ seg, segments }: { seg: DetailSegment; segments: DetailSegment[] }) {
  const opened = openedDate(seg, segments);
  return (
    <>
      <div className="wfv-sect">
        <div className="k">Stage</div>
        <div className="big">{seg.name}</div>
      </div>
      <div className="wfv-opens">
        <div className="k2">⏱ Opens when</div>
        {triggerText(seg.trigger, segments)}{opened ? ` — opened ${fmtDate(opened)}` : ""}{seg.stepsOrdered ? " · steps run in order" : ""}
      </div>
      <div className="wfv-sect">
        <div className="wfv-kv"><span>Steps</span><span className="v">{seg.stepsDone} / {seg.stepsTotal}</span></div>
      </div>
    </>
  );
}

// D-RS6 — render the declared WRITE effects of the selected element in plain
// English. `setAttribute` (the reporting write) reads as "sets <field> → <value>",
// which is exactly what lands on the matter's declared reporting fields.
function EffectsList({ effects }: { effects: DeclaredEffect[] }) {
  if (effects.length === 0) return <p className="wfv-empty">Nothing happens automatically here.</p>;
  return (
    <ul className="wfv-fx">
      {effects.map((e, i) => <li key={i}>{effectPhrase(e)}</li>)}
    </ul>
  );
}

function effectEventLabel(e: DeclaredEffect): string {
  switch (e.event) {
    case "onOpen": return "When this stage opens";
    case "onComplete": return "When this step completes";
    case "onDecide": return e.outcomeCode ? `On “${e.outcomeCode}”` : "When decided";
    case "onStart": return "When the process starts";
    default: return "";
  }
}
function effectPhrase(e: DeclaredEffect): string {
  const when = effectEventLabel(e);
  const body = e.verb === "setAttribute" && e.attribute
    ? `sets ${e.attribute.replace(/_/g, " ")} → ${e.value}`
    : e.verb;
  return when ? `${when}: ${body}` : body;
}

function WaitingList({ waits, actions, busy }: { waits: WaitingStep[]; actions: WorkflowActions; busy: boolean }) {
  if (waits.length === 0) return <p className="wfv-empty">Nothing is waiting on the outside world.</p>;
  return (
    <>
      {waits.map((w) => (
        <div className="wfv-sig" key={w.stepEntryNo}>
          <span className="b" />{w.waitingOn || "a fact about the world"} — releases “{w.name}”
          {/* RC1-4 — a wait is completable from every render point: Mark received
              releases it out-of-band (the fact arrived; a human/agent knows). */}
          <button className="btn btn--secondary btn--sm" style={{ marginLeft: 8 }} disabled={busy} onClick={() => actions.complete(w.stepEntryNo)}>Mark received</button>
        </div>
      ))}
    </>
  );
}

function MatterStats({ segments }: { segments: DetailSegment[] }) {
  let total = 0, done = 0, open = 0, waiting = 0, agents = 0;
  for (const s of segments) {
    for (const st of s.steps) {
      total++;
      if (stepIsDone(st)) done++;
      else if (st.status === "ready") { open++; if (st.assigneeKind === "agent") agents++; }
      else if (st.status === "waiting") { waiting++; if (st.assigneeKind === "agent") agents++; }
    }
  }
  return (
    <div className="wfv-sect">
      <div className="k">This matter</div>
      <div className="wfv-kv"><span>Steps done</span><span className="v">{done} / {total}</span></div>
      <div className="wfv-kv"><span>Open now</span><span className="v">{open}</span></div>
      <div className="wfv-kv"><span>Waiting</span><span className="v">{waiting}</span></div>
      <div className="wfv-kv"><span>With agents</span><span className="v">{agents} ✦</span></div>
    </div>
  );
}

// ─── the DRIVE spine ────────────────────────────────────────────────────────

function scrollToSeg(segmentNo: number) {
  document.getElementById(`seg-${segmentNo}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
}

export function WorkflowDriveLens(props: LensProps) {
  const { data, actions, busy, selected, onSelect, preview, onPreview } = props;
  const segments = data.detail.segments;
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  function toggleExpand(no: number) {
    setExpanded((prev) => { const n = new Set(prev); if (n.has(no)) n.delete(no); else n.add(no); return n; });
  }

  // The path chip = the chosen outcome of the last resolved decision.
  const lastResolved = [...segments].reverse().find((s) => s.decision?.resolved);
  const pathLabel = lastResolved?.decision?.outcomes.find((o) => o.code === lastResolved.decision?.outcomeCode)?.label ?? null;

  const previewSeg = preview?.decisionSegmentNo ?? null;
  // The FIRST previewed branch segment carries the "previewing —" crumb (computed
  // up front so the map body stays pure — no mutable render-time flag).
  const firstPreviewSegNo = preview
    ? segments.find((s) => s.trigger?.decisionSegmentNo === preview.decisionSegmentNo && s.trigger?.outcome === preview.outcome)?.segmentNo ?? null
    : null;

  return (
    <div className="wfv-body">
      <div className="wfv-spine">
        {pathLabel ? (
          <div className="wfv-pathrow"><span className="k">PATH IN VIEW</span><span className="wfv-pathchip"><span className="d" />{pathLabel}</span></div>
        ) : null}

        <div className="wfv-start"><span className="dot" /><b>Matter opens</b><span className="sub">· the single start — everything descends from here</span></div>

        {segments.map((seg) => {
          const conn = <div className="wfv-conn is-on" key={`c-${seg.segmentNo}`} />;

          // Branch classification for a segment opened by a decision outcome.
          const t = seg.trigger;
          const decNo = t?.decisionSegmentNo;
          const dec = decNo != null ? segments.find((s) => s.segmentNo === decNo)?.decision : undefined;
          const isUntaken = !!(dec?.resolved && t?.outcome && t.outcome !== dec.outcomeCode);
          const isPreviewed = !!(previewSeg != null && decNo === previewSeg && t?.outcome === preview?.outcome);
          const inBranch = decNo != null;

          const showCrumb = isPreviewed && seg.segmentNo === firstPreviewSegNo;
          const crumbLbl = dec?.outcomes.find((o) => o.code === t?.outcome)?.label ?? t?.outcome ?? "";
          const crumb = showCrumb
            ? <div className="wfv-prevk" key={`pk-${seg.segmentNo}`}><span className="d" /><b>previewing</b> — “{crumbLbl}”</div>
            : null;

          const body = renderSegment(seg, {
            segments, selected, onSelect, actions, busy, preview, onPreview,
            expanded: expanded.has(seg.segmentNo), toggleExpand,
            isUntaken, isPreviewed, dir: data.directory,
          });

          const wrapped = inBranch
            ? <div className={`wfv-branch ${isPreviewed ? "is-preview" : ""}`} key={`b-${seg.segmentNo}`}
                style={ocStyle(outcomeColorVar(dec, t?.outcome))}>{body}</div>
            : body;

          return <div key={`seg-wrap-${seg.segmentNo}`}>{conn}{crumb}{wrapped}</div>;
        })}
      </div>

      <PropertiesRail data={data} actions={actions} busy={busy} selected={selected} onSelect={onSelect} preview={preview} onPreview={onPreview} />
    </div>
  );
}

interface SegCtx {
  segments: DetailSegment[];
  selected: Selection | null;
  onSelect: (s: Selection | null) => void;
  actions: WorkflowActions;
  busy: boolean;
  preview: Preview | null;
  onPreview: (p: Preview | null) => void;
  expanded: boolean;
  toggleExpand: (no: number) => void;
  isUntaken: boolean;
  isPreviewed: boolean;
  dir: Directory;
}

function renderSegment(seg: DetailSegment, ctx: SegCtx): React.ReactNode {
  const done = seg.state === "done" || seg.state === "skipped";
  const isDecision = seg.hasDecision && seg.decision;
  const selectedSeg = ctx.selected?.segmentNo === seg.segmentNo;

  // Untaken branch (resolved decision, road not taken) — a subdued stub.
  if (ctx.isUntaken && !ctx.isPreviewed) {
    return (
      <div id={`seg-${seg.segmentNo}`} className={`wfv-seg is-stub is-selectable ${ctx.selected?.kind === "segment" && selectedSeg ? "is-selected" : ""}`}
        onClick={() => ctx.onSelect({ kind: "segment", segmentNo: seg.segmentNo })}>
        <div className="wfv-seg__h">
          <span className="wfv-seg__nm">{seg.name}</span>
          <span className="wfv-trg"><b>opens:</b> when {triggerText(seg.trigger, ctx.segments)}</span>
        </div>
      </div>
    );
  }

  // A decision segment — steps + option tiles inline.
  if (isDecision) return renderDecisionSeg(seg, ctx, selectedSeg);

  // A DONE, non-decision segment — collapsed to one dated line (expandable).
  if (done && !ctx.isPreviewed) {
    return (
      <>
        <div id={`seg-${seg.segmentNo}`} className={`wfv-done1 is-selectable ${ctx.selected?.kind === "segment" && selectedSeg ? "is-selected" : ""}`}
          onClick={() => ctx.onSelect({ kind: "segment", segmentNo: seg.segmentNo })}>
          <button className={`ex ${ctx.expanded ? "is-open" : ""}`} aria-label={ctx.expanded ? "Collapse steps" : "Expand steps"} onClick={(e) => { e.stopPropagation(); ctx.toggleExpand(seg.segmentNo); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M9 18l6-6-6-6" /></svg>
          </button>
          <span className="tick"><TickGlyph /></span>
          <span className="nm">{seg.name}</span>
          <span className="meta">{seg.stepsTotal} step{seg.stepsTotal === 1 ? "" : "s"} · {seg.state === "skipped" ? "skipped" : "done"}{seg.completedAt ? ` ${fmtDate(seg.completedAt)}` : ""}</span>
        </div>
        {ctx.expanded ? (
          <div className="wfv-doneexp">{seg.steps.map((st) => <StepRow key={st.stepEntryNo} seg={seg} step={st} ctx={ctx} />)}</div>
        ) : null}
      </>
    );
  }
  // An OPEN / WAITING / previewed segment — full card with step rows.
  const hatch = ctx.isPreviewed ? "is-hatch" : "";
  const isFrontier = seg.state === "open";
  return (
    <div id={`seg-${seg.segmentNo}`} className={`wfv-seg is-selectable ${hatch} ${ctx.selected?.kind === "segment" && selectedSeg ? "is-selected" : ""}`}
      style={seg.state === "waiting" && !ctx.isPreviewed ? { opacity: 0.7 } : undefined}
      onClick={() => ctx.onSelect({ kind: "segment", segmentNo: seg.segmentNo })}>
      <div className="wfv-seg__h" onClick={(e) => { e.stopPropagation(); ctx.onSelect({ kind: "segment", segmentNo: seg.segmentNo }); }}>
        <span className="wfv-seg__nm">{seg.name}</span>
        {isFrontier && !ctx.isPreviewed ? <span className="wfv-seg__here">here</span> : null}
        <span className="wfv-trg"><b>opens:</b> when {triggerText(seg.trigger, ctx.segments)}</span>
      </div>
      <div className="wfv-steps">
        {seg.steps.map((st) => <StepRow key={st.stepEntryNo} seg={seg} step={st} ctx={ctx} />)}
      </div>
    </div>
  );
}

function renderDecisionSeg(seg: DetailSegment, ctx: SegCtx, selectedSeg: boolean): React.ReactNode {
  const dec = seg.decision!;
  const chosen = dec.outcomeCode;
  const previewOutcome = ctx.preview?.decisionSegmentNo === seg.segmentNo ? ctx.preview.outcome : null;
  const previewLabel = dec.outcomes.find((o) => o.code === previewOutcome)?.label ?? "";
  const isSel = ctx.selected?.kind === "decision" && selectedSeg;

  return (
    <div id={`seg-${seg.segmentNo}`} className={`wfv-seg is-decision is-selectable ${isSel ? "is-selected" : ""}`}
      onClick={() => ctx.onSelect({ kind: "decision", segmentNo: seg.segmentNo })}>
      <div className="wfv-seg__h">
        <span className="wfv-seg__nm">{seg.name}</span>
        {dec.resolved
          ? <span className="wfv-seg__chosen">chosen: {dec.outcomes.find((o) => o.code === chosen)?.label ?? chosen}</span>
          : null}
        <span className={`wfv-seg__decpill ${dec.resolved ? "" : "is-open"}`}>{dec.resolved ? "DECIDED" : dec.ripe ? "NEEDS YOU" : "PENDING"}</span>
      </div>
      {seg.steps.length > 0 ? (
        <div className="wfv-steps">{seg.steps.map((st) => <StepRow key={st.stepEntryNo} seg={seg} step={st} ctx={ctx} />)}</div>
      ) : null}
      <div className="wfv-opts">
        {dec.outcomes.map((o) => {
          const isChosen = o.code === chosen;
          const isPrev = o.code === previewOutcome;
          return (
            <button key={o.code} className={`wfv-opt ${isChosen ? "is-chosen" : ""} ${isPrev ? "is-preview" : ""} ${dec.resolved && !isChosen && !isPrev ? "is-untaken" : ""}`}
              style={ocStyle(outcomeColorVar(dec, o.code))}
              disabled={ctx.busy && !dec.resolved}
              onClick={(e) => {
                e.stopPropagation();
                ctx.onSelect({ kind: "decision", segmentNo: seg.segmentNo });
                if (!dec.resolved) { if (dec.ripe) ctx.actions.recordOutcome(seg.segmentNo, o.code); return; }
                if (isChosen || isPrev) ctx.onPreview(null);
                else ctx.onPreview({ decisionSegmentNo: seg.segmentNo, outcome: o.code });
              }}>
              <div className="k">{o.label.toUpperCase()} {isChosen ? <span className="ch">✓ CHOSEN</span> : null}</div>
              <div className="nmm">{o.label}</div>
              <div className="cont">{dec.resolved ? (isChosen ? "↓ the road taken" : isPrev ? "↓ preview" : "↓ not taken") : dec.ripe ? "click to decide" : "finish the stage first"}</div>
            </button>
          );
        })}
      </div>
      {dec.resolved ? (
        <div className="wfv-decact" onClick={(e) => e.stopPropagation()}>
          <button className="btn btn--primary btn--sm" style={{ flex: 1 }} disabled={ctx.busy || !previewOutcome}
            onClick={() => { if (previewOutcome) { ctx.actions.recordOutcome(seg.segmentNo, previewOutcome); ctx.onPreview(null); } }}>
            {previewOutcome ? `Change to “${previewLabel}”` : "Preview an option to switch"}
          </button>
          <button className="btn btn--secondary btn--sm" disabled={ctx.busy} onClick={() => { ctx.actions.clearOutcome(seg.segmentNo); ctx.onPreview(null); }}>Clear choice</button>
        </div>
      ) : null}
    </div>
  );
}

function StepRow({ seg, step, ctx }: { seg: DetailSegment; step: DetailStep; ctx: SegCtx }) {
  const done = stepIsDone(step);
  const ready = step.status === "ready";
  const asg = resolveAssignee(step.assigneeKind, step.assigneeCode, ctx.dir);
  const isSel = ctx.selected?.kind === "step" && ctx.selected.stepEntryNo === step.stepEntryNo;
  const overdue = ready && isOverdue(step.dueDate);
  const dueTxt = step.dueDate ? (done ? fmtDate(step.completedAt ?? step.dueDate) : `due ${fmtDate(step.dueDate)}`) : "";

  return (
    <div className={`wfv-strow is-clickable ${done ? "is-done" : ""} ${ready ? "is-ready" : ""} ${isSel ? "is-selected" : ""}`}
      onClick={(e) => { e.stopPropagation(); ctx.onSelect({ kind: "step", segmentNo: seg.segmentNo, stepEntryNo: step.stepEntryNo }); }}>
      {done ? <span className="tick"><TickGlyph /></span> : <span className={`bullet ${ready ? "is-ready" : ""}`} />}
      <span className="t">{step.name}</span>
      <span className={`w ${overdue ? "is-od" : ""}`}>
        {step.assigneeKind ? (
          <>
            <span className={`wfv-avm ${asg.isAgent ? "is-agent" : ""}`} style={agentAvatarStyle(step.assigneeKind, step.assigneeCode)}>{asg.isAgent ? "✦" : asg.initials}</span>
            {asg.who}
          </>
        ) : <span style={{ fontStyle: "italic" }}>unassigned</span>}
        {done && step.completedAt ? ` · ${fmtDate(step.completedAt)}` : dueTxt ? ` · ${dueTxt}` : ""}
      </span>
    </div>
  );
}
